import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDateTime } from '../hooks/useDateTime';
import { useAuth } from '../hooks/useAuth';
import { menuItems, socialLinks } from '../utils/data';
import { MenuItem } from '../types';
import LoginModal from './LoginModal';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const dateTime = useDateTime();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  // Inicializar UIkit dropdowns cuando el componente se monta
  React.useEffect(() => {
    // Asegurar que UIkit esté disponible
    if (typeof window !== 'undefined' && (window as any).UIkit) {
      const UIkit = (window as any).UIkit;
      
      // Inicializar dropdowns después de que el DOM esté listo
      setTimeout(() => {
        UIkit.util.ready(() => {
          // Inicializar todos los dropdowns
          const dropdownElements = document.querySelectorAll('[data-uk-dropdown]');
          dropdownElements.forEach(element => {
            UIkit.dropdown(element, {
              mode: 'click',
              pos: 'bottom-left',
              offset: 0
            });
          });
        });
      }, 100);
    }
  }, []);

    const renderMenuItem = (item: MenuItem) => {
    if (item.children) {
      return (
        <li key={item.id} className="uk-parent">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a 
            href="#" 
            data-uk-dropdown="mode: click; pos: bottom-left"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {item.label}
            <i className="fas fa-angle-down"></i>
          </a>
          <div className="uk-dropdown uk-dropdown-navbar">
            <ul className="uk-nav uk-nav-dropdown">
              {item.children.map(child => (
                <li key={child.id} className={child.children ? 'uk-parent' : ''}>
                  {child.href ? (
                    <Link to={child.href}>
                      {child.label}
                      {child.children && <i className="fas fa-angle-right"></i>}
                    </Link>
                  ) : (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a 
                      href="#" 
                      data-uk-dropdown="mode: click; pos: right-top"
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      {child.label}
                      {child.children && <i className="fas fa-angle-right"></i>}
                    </a>
                  )}
                  {child.children && (
                    <div className="uk-dropdown uk-dropdown-navbar">
                      <ul className="uk-nav uk-nav-dropdown">
                        {child.children.map(subChild => (
                          <li key={subChild.id}>
                            <Link to={subChild.href || '#'}>
                              {subChild.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </li>
      );
    }

    // Función especial para el botón INICIO
    if (item.id === 'inicio') {
      const handleInicioClick = (e: React.MouseEvent) => {
        e.preventDefault();
        
        // Navegar a la página de inicio usando React Router
        navigate('/');
        
        // Reactivar el popup de bienvenida
        setTimeout(() => {
          // Disparar un evento personalizado para mostrar el popup
          const event = new CustomEvent('showWelcomePopup');
          document.dispatchEvent(event);
        }, 100);
      };

      return (
        <li key={item.id} className="uk-active">
          <a href="/" onClick={handleInicioClick} style={{ color: 'inherit', textDecoration: 'none' }}>
            {item.label}
          </a>
        </li>
      );
    }

    return (
      <li key={item.id} className="uk-active">
        <Link to={item.href || '#'}>
          {item.label}
        </Link>
      </li>
    );
  };

  return (
    <header className="page-header" style={{ position: 'fixed', width: '100%', zIndex: 999, top: 0 }}>
      <style>
        {`
          /* ========================================
             NAVBAR REDISEÑADO - MÁS DELGADO Y ELEGANTE
             ======================================== */
          
          /* Header superior más delgado */
          .page-header-top {
            background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
            color: white !important;
            padding: 6px 0 !important;
            font-size: 12px;
            font-weight: 400;
            letter-spacing: 0.3px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }
          
          .page-header-top a {
            color: rgba(255, 255, 255, 0.9) !important;
            transition: all 0.3s ease;
            text-decoration: none;
            font-weight: 400;
          }
          
          .page-header-top a:hover {
            color: #20D3C7 !important;
            text-decoration: none;
          }
          
          /* Novedades más compactas */
          .page-header-top .update {
            font-size: 11px;
            line-height: 1.3;
          }
          
          .page-header-top .update i {
            font-size: 10px;
            margin-right: 6px;
            color: #20D3C7;
          }
          
          /* Fecha y hora más elegante */
          .page-header-top .time-work {
            color: rgba(255, 255, 255, 0.8) !important;
            font-size: 11px;
            font-weight: 300;
            letter-spacing: 0.2px;
          }
          
          .page-header-top .time-work i {
            font-size: 10px;
            margin-right: 6px;
            color: #20D3C7;
          }
          
          /* Enlaces de login/registro más elegantes */
          .page-header-top .login-link,
          .page-header-top .register-link {
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .page-header-top .login-link i,
          .page-header-top .register-link i {
            font-size: 10px;
            margin-right: 6px;
            color: #20D3C7;
          }
          
          .page-header-top .gratis-highlight {
            color: #20D3C7 !important;
            font-weight: 700;
            font-size: 12px;
          }
          
          /* Redes sociales más pequeñas */
          .page-header-top .social-list__link {
            color: rgba(255, 255, 255, 0.7) !important;
            transition: all 0.3s ease;
            font-size: 12px;
            padding: 4px;
          }
          
          .page-header-top .social-list__link:hover {
            color: #20D3C7 !important;
            transform: translateY(-1px);
          }
          
          /* Header inferior más delgado */
          .page-header-bottom {
            background: rgba(255, 255, 255, 0.98) !important;
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(0, 0, 0, 0.08);
            padding: 8px 0 !important;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          }
          
          /* Logo más compacto */
          .page-header-bottom .logo__img {
            max-width: 120px !important;
            height: auto;
            transition: all 0.3s ease;
          }
          
          .page-header-bottom .logo__img:hover {
            transform: scale(1.02);
          }
          
          /* Soporte más elegante */
          .page-header-bottom .support__link {
            display: flex;
            align-items: center;
            gap: 8px;
            text-decoration: none;
            color: #333;
            transition: all 0.3s ease;
          }
          
          .page-header-bottom .support__icon {
            font-size: 18px;
            color: #20D3C7;
            transition: all 0.3s ease;
          }
          
          .page-header-bottom .support__label {
            font-size: 10px;
            color: #666;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            line-height: 1.2;
          }
          
          .page-header-bottom .support__phone {
            font-size: 13px;
            font-weight: 600;
            color: #333;
            letter-spacing: 0.3px;
          }
          
          .page-header-bottom .support__link:hover .support__icon {
            transform: scale(1.1);
            color: #17BCBA;
          }
          
          /* Navegación más elegante */
          .uk-navbar-nav > li > a {
            color: #555 !important;
            font-size: 13px;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 8px 12px !important;
            transition: all 0.3s ease;
            border-radius: 4px;
            margin: 0 2px;
          }
          
          .uk-navbar-nav > li > a:hover {
            color: #20D3C7 !important;
            background: rgba(32, 211, 199, 0.1);
            transform: translateY(-1px);
          }
          
          .uk-navbar-nav > li.uk-active > a {
            color: #20D3C7 !important;
            background: rgba(32, 211, 199, 0.15);
            font-weight: 600;
          }
          
          /* Dropdowns más elegantes */
          .uk-dropdown {
            background: white;
            border: 1px solid rgba(0, 0, 0, 0.08);
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            border-radius: 8px;
            min-width: 220px;
            margin-top: 8px !important;
          }
          
          .uk-nav-dropdown > li > a {
            color: #555 !important;
            font-size: 13px;
            font-weight: 400;
            padding: 10px 16px !important;
            transition: all 0.3s ease;
            border-bottom: 1px solid rgba(0, 0, 0, 0.05);
          }
          
          .uk-nav-dropdown > li > a:hover {
            color: #20D3C7 !important;
            background: rgba(32, 211, 199, 0.08);
            padding-left: 20px;
          }
          
          .uk-nav-dropdown > li:last-child > a {
            border-bottom: none;
          }
          
          /* Iconos de dropdown más pequeños */
          .uk-nav-dropdown > li.uk-parent > a .fas {
            font-size: 10px;
            right: 12px;
          }
          
          /* Información de usuario más compacta */
          .page-header-top .user-info {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          
          .page-header-top .user-details {
            text-align: right;
          }
          
          .page-header-top .user-details div:first-child {
            font-size: 11px;
            font-weight: 600;
            color: white;
            line-height: 1.2;
          }
          
          .page-header-top .user-details div:last-child {
            font-size: 10px;
            color: rgba(255, 255, 255, 0.7);
            line-height: 1.2;
          }
          
          .page-header-top .user-actions {
            display: flex;
            gap: 6px;
          }
          
          .page-header-top .user-actions a,
          .page-header-top .user-actions button {
            padding: 4px 8px !important;
            font-size: 10px !important;
            font-weight: 500 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.3px !important;
            border-radius: 4px !important;
            text-decoration: none !important;
            transition: all 0.3s ease !important;
          }
          
          .page-header-top .user-actions a {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
            color: white !important;
          }
          
          .page-header-top .user-actions button {
            background: #dc2626 !important;
            color: white !important;
            border: none !important;
            cursor: pointer !important;
          }
          
          .page-header-top .user-actions a:hover,
          .page-header-top .user-actions button:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          }
          
          /* Espaciado general más compacto */
          .page-header-top__left,
          .page-header-top__right,
          .page-header-bottom__left,
          .page-header-bottom__right {
            padding: 0 12px;
          }
          
          /* Espacio para el contenido principal ajustado */
          .page-wrapper {
            margin-top: 80px !important;
          }
          
          /* Responsive para navbar más delgado */
          @media (max-width: 959px) {
            .page-wrapper {
              margin-top: 70px !important;
            }
            
            .page-header-top {
              padding: 4px 0 !important;
              font-size: 10px;
            }
            
            .page-header-bottom {
              padding: 6px 0 !important;
            }
            
            .page-header-bottom .logo__img {
              max-width: 100px !important;
            }
            
            .uk-navbar-nav > li > a {
              font-size: 12px;
              padding: 6px 10px !important;
            }
          }
          
          /* Animaciones suaves */
          .page-header-top,
          .page-header-bottom {
            transition: all 0.3s ease;
          }
          
          /* Efectos de hover mejorados */
          .page-header-top .update:hover,
          .page-header-top .time-work:hover,
          .page-header-top .login-link:hover,
          .page-header-top .register-link:hover {
            transform: translateY(-1px);
          }
        `}
      </style>
      
      <div className="page-header-top">
        <div className="page-header-top__left">
          <div className="update">
            <i className="far fa-hand-point-right"></i>
            <span>
              <a href="#!">Últimas Novedades</a> Obtén hasta un 45% de descuento en las mejores ofertas de equipos de construcción en Buen Aire
            </span>
          </div>
        </div>
        <div className="page-header-top__right">
          <div className="time-work icon">
            <i className="far fa-clock"></i>
            <span>{dateTime}</span>
          </div>
          {!isAuthenticated ? (
            <>
              <div className="login-link">
                <i className="fas fa-sign-in-alt"></i>
                <button 
                  onClick={() => setIsLoginModalOpen(true)}
                  style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'none' }}
                >
                  Iniciar Sesión
                </button>
              </div>
              <div className="register-link">
                <i className="fas fa-building"></i>
                <Link to="/registro">
                  Dar de alta mi empresa - <span className="gratis-highlight">GRATIS</span>
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div className="user-details" style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                    {user?.nombreFantasia}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>
                    {user?.email}
                  </div>
                </div>
                <div className="user-actions" style={{ display: 'flex', gap: '0.5rem' }}>
                  <Link 
                    to="/dashboard" 
                    style={{ 
                      padding: '0.5rem 1rem', 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                      color: 'white', 
                      borderRadius: '0.5rem', 
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                  >
                    <i className="fas fa-tachometer-alt"></i> Dashboard
                  </Link>
                  <button 
                    onClick={logout}
                    style={{ 
                      padding: '0.5rem 1rem', 
                      background: '#dc2626', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '0.5rem', 
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                  >
                    <i className="fas fa-sign-out-alt"></i> Salir
                  </button>
                </div>
              </div>
            </>
          )}
          <div className="social">
            <ul className="social-list">
              {socialLinks.map(social => (
                <li key={social.platform} className="social-list__item">
                  <a className="social-list__link" href={social.href}>
                    <i className={social.icon}></i>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      
             <div className="page-header-bottom">
        <div className="page-header-bottom__left">
          <div className="logo">
            <Link className="logo__link" to="/">
              <img className="logo__img" src="/assets/img/logo_buenaire.png" alt="Buen Aire" style={{ maxWidth: '150px' }} />
            </Link>
          </div>
          <div className="support">
            <a className="support__link" href="tel:+5491133102465">
              <div className="support__icon">
                <i className="fas fa-headset"></i>
              </div>
              <div className="support__desc">
                <div className="support__label">Obtén Soporte Rápido</div>
                <div className="support__phone">+54 9 11 3310-2465</div>
              </div>
            </a>
          </div>
        </div>
        
        <div className="page-header-bottom__right">
          <nav className="uk-navbar-container uk-navbar-transparent" data-uk-navbar>
            <div className="nav-overlay uk-visible@l">
              <ul className="uk-navbar-nav">
                {menuItems.map(renderMenuItem)}
              </ul>
            </div>
          </nav>
          
          <a 
            className="uk-navbar-toggle uk-hidden@l" 
            href="#mobile-menu" 
            data-uk-toggle
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <span data-uk-icon="menu"></span>
          </a>
        </div>
      </div>
      
      {/* Menú móvil */}
      <div id="mobile-menu" className="uk-offcanvas" data-uk-offcanvas="mode: push; overlay: true">
        <div className="uk-offcanvas-bar">
          <button className="uk-offcanvas-close" type="button" data-uk-close></button>
          <h3 className="uk-margin-bottom">Menú</h3>
          <ul className="uk-nav uk-nav-default">
            {menuItems.map(item => (
              <li key={item.id}>
                <Link to={item.href || '#'}>{item.label}</Link>
                {item.children && (
                  <ul className="uk-nav-sub">
                    {item.children.map(child => (
                      <li key={child.id}>
                        <Link to={child.href || '#'}>{child.label}</Link>
                        {child.children && (
                          <ul className="uk-nav-sub">
                            {child.children.map(subChild => (
                              <li key={subChild.id}>
                                <Link to={subChild.href || '#'}>{subChild.label}</Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          
          {/* Enlaces adicionales en móvil */}
          <div className="uk-margin-large-top">
            <h4>Contacto</h4>
            <p><i className="fas fa-phone"></i> +54 9 11 3310-2465</p>
            <p><i className="fas fa-clock"></i> {dateTime}</p>
          </div>
          
          <div className="uk-margin-top">
            <h4>Síguenos</h4>
            <div className="uk-flex uk-flex-wrap">
              {socialLinks.map(social => (
                <a 
                  key={social.platform} 
                  className="uk-margin-small-right social-list__link" 
                  href={social.href}
                  style={{ fontSize: '20px' }}
                >
                  <i className={social.icon}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal de Login */}
      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSuccess={() => {
          setIsLoginModalOpen(false);
          // Opcional: mostrar mensaje de éxito o redirigir
        }}
      />
    </header>
  );
};

export default Header;
